import type { TableSoundCue, TableSoundKind } from '@v2/contracts'
import { loadSoundBank, soundSlice, type InterfaceSound, type PhysicalInteractionSound, type RecordedSound } from './sound-bank'

export const SOUND_PREFERENCE_KEY = 'tabletop-v2:sound-muted'

export interface SoundPreferenceStorage {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
}

export function soundStorage(): SoundPreferenceStorage | null {
  try { return globalThis.localStorage ?? null } catch { return null }
}

export function readSoundMuted(storage: SoundPreferenceStorage | null): boolean {
  try { return storage?.getItem(SOUND_PREFERENCE_KEY) === 'true' } catch { return false }
}

/** Pure occurrence tracking. Suppressed sounds are consumed, never queued. */
export class SoundCueTracker {
  private seen = new Set<string>()
  private initialized = false
  private lastBurst = -Infinity

  update(cues: readonly TableSoundCue[] | undefined, now: number, enabled = true, baseline = false): readonly TableSoundKind[] {
    const silent = baseline || !this.initialized
    if (silent) this.reset()
    this.initialized = true
    const fresh = new Set<TableSoundKind>()
    // Bound even a mistakenly projected full event log; the newest cues win.
    for (const cue of (cues ?? []).slice(-128)) {
      if (this.seen.has(cue.id)) continue
      this.seen.add(cue.id)
      fresh.add(cue.sound)
    }
    // Cues represent recent occurrences, not an ever-growing event log.
    while (this.seen.size > 512) this.seen.delete(this.seen.values().next().value!)
    if (silent || !enabled || fresh.size === 0 || now - this.lastBurst < 120) return []
    this.lastBurst = now
    // Many cards drawn at once make one deal sound. A noisy batch stays small.
    return [...fresh].slice(0, 3)
  }

  reset(): void {
    this.seen.clear()
    this.initialized = false
    this.lastBurst = -Infinity
  }
}

export interface TableAudioOptions {
  readonly storage?: SoundPreferenceStorage | null
  /** Dependency injection also makes an unavailable audio device harmless. */
  readonly createContext?: () => AudioContext | null
  readonly now?: () => number
  readonly loadBank?: typeof loadSoundBank
}

type Voice = { readonly source: AudioScheduledSourceNode; readonly nodes: readonly AudioNode[] }

/** Instance-owned recorded sounds, with offline synthesis as a loading/failure fallback. */
export class TableAudio {
  private readonly tracker = new SoundCueTracker()
  private readonly storage: SoundPreferenceStorage | null
  private readonly createContext: () => AudioContext | null
  private readonly now: () => number
  private context: AudioContext | null = null
  private master: GainNode | null = null
  private noise: AudioBuffer | null = null
  private voices = new Set<Voice>()
  private hoverVoice: Voice | null = null
  private disposed = false
  private unavailable = false
  private unlocking: Promise<void> | null = null
  private isMuted: boolean
  private bank: AudioBuffer | null = null
  private bankLoading: Promise<void> | null = null
  private readonly bankAbort = new AbortController()
  private readonly loadBank: typeof loadSoundBank
  private variations = new Map<RecordedSound, number>()
  private lastHover = -Infinity
  private lastInteraction = -Infinity

  constructor(options: TableAudioOptions = {}) {
    this.loadBank = options.loadBank ?? loadSoundBank
    this.storage = options.storage === undefined ? soundStorage() : options.storage
    this.createContext = options.createContext ?? createAudioContext
    this.now = options.now ?? (() => performance.now())
    this.isMuted = readSoundMuted(this.storage)
  }

  get muted(): boolean { return this.isMuted }
  get status(): 'locked' | 'ready' | 'muted' | 'unavailable' | 'disposed' {
    if (this.disposed) return 'disposed'
    if (this.isMuted) return 'muted'
    if (this.unavailable) return 'unavailable'
    return this.context?.state === 'running' ? 'ready' : 'locked'
  }

  /** Call from a trusted pointer/key gesture, including shared DOM controls. */
  unlock(): Promise<void> {
    if (this.disposed || this.isMuted || this.unavailable) return Promise.resolve()
    if (this.unlocking) return this.unlocking
    this.unlocking = this.open().finally(() => { this.unlocking = null })
    return this.unlocking
  }

  private async open(): Promise<void> {
    try {
      if (!this.context) {
        this.context = this.createContext()
        if (!this.context) { this.unavailable = true; return }
        this.master = this.context.createGain()
        this.master.gain.value = 0.34
        this.master.connect(this.context.destination)
        this.noise = makeNoise(this.context)
      }
      if (this.context.state === 'suspended') await this.context.resume()
      if (!this.disposed && !this.bank && !this.bankLoading) {
        const context = this.context
        this.bankLoading = this.loadBank(context, this.bankAbort.signal).then(bank => {
          if (!this.disposed) this.bank = bank
        }).catch(() => { /* Keep synthesized gameplay sounds when assets are unavailable. */ })
      }
    } catch {
      // A denied autoplay/device request can be retried on the next gesture.
      // Audio must never reject an action or break table initialization.
    }
  }

  update(cues: readonly TableSoundCue[] | undefined, baseline = false): void {
    if (this.disposed) return
    if (baseline) this.stopVoices()
    const sounds = this.tracker.update(cues, this.now(), this.status === 'ready', baseline)
    sounds.forEach((sound, index) => {
      try { this.play(sound, index * 0.025) } catch { this.stopVoices() }
    })
  }

  /** Local presentation only: never projected, queued, or replayed on load/unmute. */
  interaction(sound: InterfaceSound | PhysicalInteractionSound): void {
    if (this.status !== 'ready' || !this.bank) return
    const now = this.now()
    if (sound === 'hover' || sound === 'card-hover' || sound === 'piece-hover') {
      if (now - this.lastHover < 190 || now - this.lastInteraction < 160) return
      this.lastHover = now
    } else {
      if (now - this.lastInteraction < 65) return
      this.lastInteraction = now
    }
    try { this.recorded(sound, 0) } catch { /* Optional feedback never blocks input. */ }
  }

  /** A soft rising two-note chime. Original synthesis; shares mute and device lifetime. */
  turn(): void {
    if (this.status !== 'ready' || !this.context || !this.master) return
    try {
      const context = this.context
      for (const [index, frequency] of [659.25, 880].entries()) {
        const start = context.currentTime + 0.01 + index * 0.16
        const tone = context.createOscillator(), gain = context.createGain()
        tone.type = 'sine'
        tone.frequency.value = frequency
        gain.gain.setValueAtTime(0, start)
        gain.gain.linearRampToValueAtTime(0.22, start + 0.012)
        gain.gain.exponentialRampToValueAtTime(0.001, start + 0.32)
        tone.connect(gain).connect(this.master)
        this.track(tone, [gain])
        tone.start(start)
        tone.stop(start + 0.34)
      }
    } catch { /* Optional notification audio must never interrupt play. */ }
  }

  setMuted(muted: boolean): void {
    this.isMuted = muted
    if (muted) this.stopVoices()
    try { this.storage?.setItem(SOUND_PREFERENCE_KEY, String(muted)) } catch { /* Storage may be disabled. */ }
  }

  /** Clear private/transient presentation on a new viewer or table. */
  reset(): void {
    this.tracker.reset()
    this.lastHover = this.lastInteraction = -Infinity
    this.stopVoices()
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    this.reset()
    this.master?.disconnect()
    this.master = null
    this.noise = null
    this.bankAbort.abort()
    this.bank = null
    const context = this.context
    this.context = null
    if (context && context.state !== 'closed') void context.close().catch(() => {})
  }

  private recorded(sound: RecordedSound, delay: number): void {
    const context = this.context
    if (!context || !this.master || !this.bank) return
    const hover = sound === 'hover' || sound === 'card-hover' || sound === 'piece-hover'
    if (hover && this.hoverVoice) {
      try { this.hoverVoice.source.stop() } catch { /* Already ended. */ }
      this.release(this.hoverVoice)
    }
    if (this.voices.size >= 8) return
    const variation = this.variations.get(sound) ?? 0
    this.variations.set(sound, variation + 1)
    const clip = soundSlice(sound, variation)
    const source = context.createBufferSource(), gain = context.createGain()
    source.buffer = this.bank
    const time = context.currentTime + 0.006 + delay
    gain.gain.setValueAtTime(0, time)
    gain.gain.linearRampToValueAtTime(clip.gain, time + clip.attack)
    gain.gain.setValueAtTime(clip.gain, time + Math.max(clip.attack, clip.duration - clip.release))
    gain.gain.linearRampToValueAtTime(0, time + clip.duration)
    const nodes: AudioNode[] = [gain]
    if (clip.lowpass) {
      const filter = context.createBiquadFilter()
      filter.type = 'lowpass'
      filter.frequency.value = clip.lowpass
      filter.Q.value = 0.5
      source.connect(filter).connect(gain)
      nodes.push(filter)
    } else source.connect(gain)
    gain.connect(this.master)
    const voice = this.track(source, nodes)
    if (hover) this.hoverVoice = voice
    source.start(time, clip.offset, clip.duration)
    source.stop(time + clip.duration + 0.005)
  }

  private play(sound: TableSoundKind, delay: number): void {
    if (this.bank) {
      this.recorded(sound, delay)
      if (sound === 'shuffle') {
        this.recorded(sound, delay + 0.18)
        this.recorded(sound, delay + 0.38)
      }
      return
    }
    const context = this.context
    if (!context || !this.master || !this.noise) return
    const time = context.currentTime + 0.006 + delay
    switch (sound) {
      case 'deal':
        this.paper(time, 0.115, 0.19, 2200, 5100)
        this.tap(time + 0.105, 0.065, 680)
        break
      case 'shuffle':
        for (let index = 0; index < 4; index++) this.paper(time + index * 0.065, 0.09, 0.13, 2900 + index * 170, 4800 - index * 160)
        this.tap(time + 0.285, 0.055, 760)
        break
      case 'place':
        this.paper(time, 0.055, 0.11, 2200, 1500)
        this.tap(time + 0.018, 0.105, 520)
        break
      case 'token':
        this.tap(time, 0.14, 820)
        this.tap(time + 0.026, 0.035, 1360)
        break
      case 'reveal':
        this.paper(time, 0.095, 0.16, 1700, 4500)
        this.paper(time + 0.082, 0.09, 0.12, 4200, 1800)
        this.tap(time + 0.15, 0.08, 640)
        break
    }
  }

  private paper(time: number, duration: number, volume: number, startFrequency: number, endFrequency: number): void {
    const context = this.context!, source = context.createBufferSource(), filter = context.createBiquadFilter(), gain = context.createGain()
    source.buffer = this.noise
    filter.type = 'bandpass'
    filter.Q.value = 0.55
    filter.frequency.setValueAtTime(startFrequency, time)
    filter.frequency.exponentialRampToValueAtTime(endFrequency, time + duration)
    gain.gain.setValueAtTime(0, time)
    gain.gain.linearRampToValueAtTime(volume, time + duration * 0.2)
    gain.gain.exponentialRampToValueAtTime(0.001, time + duration)
    source.connect(filter).connect(gain).connect(this.master!)
    this.track(source, [filter, gain])
    source.start(time, (time * 0.137) % 0.6)
    source.stop(time + duration + 0.005)
  }

  private tap(time: number, volume: number, frequency: number): void {
    const context = this.context!, source = context.createBufferSource(), filter = context.createBiquadFilter(), gain = context.createGain()
    source.buffer = this.noise
    filter.type = 'bandpass'
    filter.Q.value = 2.1
    filter.frequency.value = frequency
    gain.gain.setValueAtTime(0, time)
    gain.gain.linearRampToValueAtTime(volume, time + 0.002)
    gain.gain.exponentialRampToValueAtTime(0.001, time + 0.074)
    source.connect(filter).connect(gain).connect(this.master!)
    this.track(source, [filter, gain])
    source.start(time, 0.4)
    source.stop(time + 0.08)
  }

  private track(source: AudioScheduledSourceNode, nodes: readonly AudioNode[]): Voice {
    const voice = { source, nodes }
    this.voices.add(voice)
    source.onended = () => this.release(voice)
    return voice
  }

  private release(voice: Voice): void {
    if (this.hoverVoice === voice) this.hoverVoice = null
    voice.source.onended = null
    voice.source.disconnect()
    voice.nodes.forEach(node => node.disconnect())
    this.voices.delete(voice)
  }

  private stopVoices(): void {
    for (const voice of this.voices) {
      try { voice.source.stop() } catch { /* The source may already have ended. */ }
      this.release(voice)
    }
  }
}

function createAudioContext(): AudioContext | null {
  const Audio = globalThis.AudioContext ?? (globalThis as typeof globalThis & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  return Audio ? new Audio() : null
}

function makeNoise(context: BaseAudioContext): AudioBuffer {
  const buffer = context.createBuffer(1, context.sampleRate, context.sampleRate)
  const samples = buffer.getChannelData(0)
  let seed = 0x73ad9451
  for (let index = 0; index < samples.length; index++) {
    seed ^= seed << 13; seed ^= seed >>> 17; seed ^= seed << 5
    samples[index] = (seed >>> 0) / 0xffffffff * 2 - 1
  }
  return buffer
}
